/**
 * Transforme l'ensemble des enfants en un tableau unique
 * En effet, dans un composant le props children est soit :
 *  - un composant simple
 *  - un tableau qui peut contenir :
 *      - des composants simples
 *      - des tableaux de composant
 *      - un mix des deux!!
 * @param {Component | Array<Component>} componentChildren les composants enfants
 * @returns {Array<Component>}
 */
var collectChildren = function collectChildren(componentChildren) {
  if (!Array.isArray(componentChildren)) {
    // eslint-disable-next-line no-param-reassign
    componentChildren = [componentChildren];
  }
  return componentChildren.reduce(function (children, child) {
    if (Array.isArray(child)) {
      child.forEach(function (c) {
        return children.push(c);
      });
    } else {
      children.push(child);
    }
    return children;
  }, []);
};

/**
 *
 * Permet de créer un composant "virtuel" dont on pourra récupérer les propriétés
 * et les enfants
 * Utile dans les cas où :
 *  - le composant ne possède pas de logique forte ;
 *  - vous souhaitez que l'utilisateur de votre composant puisse passer différentes
 *      types d'enfant (évite le passage de composant par propriété).
 *
 */
var Slot = function Slot() {
  var fn = function fn() {
    return null;
  };

  /**
     * Méthode permettant de récupérer les propriétés passez au Slot
     * @param {Component} children les enfants de votre composant
     * @param {boolean} all permet d'indiquer si le Slot
     * peut être présent plusieurs fois dans les enfants de votre composants
     * @param {Object | Array<Object>} _default valeur retourné si le Slot
     * n'est pas présent parmi les enfants (de préférence un Object ou un Array)
     * @returns {Object | Array<Object>}
     */
  fn.props = function (children, all, _default) {
    if (all === void 0) {
      all = false;
    }
    if (_default === void 0) {
      _default = undefined;
    }
    // eslint-disable-next-line no-param-reassign
    if (!_default) _default = all ? [] : {};
    if (!children) return _default;

    // eslint-disable-next-line no-param-reassign
    children = collectChildren(children);
    if (all) {
      var childrenComponent = children.filter(function (child) {
        return child && child.type === fn;
      });
      if (!childrenComponent.length) {
        return _default;
      }
      return childrenComponent.map(function (childComponent) {
        return childComponent.props;
      });
    }
    var childComponent = children.find(function (child) {
      return child && child.type === fn;
    });
    return childComponent ? childComponent.props : _default;
  };

  /**
     * Méthode permettant de récupérer tous les composants "enfants" de votre Slot
     * @param {Component} children les enfants de votre composant
     * @param {boolean} all permet d'indiquer si le Slot
     * peut être présent plusieurs fois dans les enfants de votre composants
     * @param {Component || Array<Component>} _default valeur retourné si le Slot
     * n'est pas présent parmi les enfants (de préférence un Component ou un tableau de Component)
     * @returns { Component || Array<Component> }
     */
  fn.get = function (children, all, _default) {
    if (all === void 0) {
      all = false;
    }
    if (_default === void 0) {
      _default = null;
    }
    var props = fn.props(children, all);
    if (all) {
      var components = props.map(function (prop) {
        return prop.children;
      }).filter(function (c) {
        return !!c;
      });
      if (_default && !components.length) {
        return _default;
      }
      return components;
    }
    return props.children ? props.children : _default;
  };

  /**
     * Retourne tous les composants parmi les enfants qui ne correspondent pas à votre Slot
     * @param {Component} children les enfants de votre composant
     * @returns @returns { Array<Component> }
     */
  fn.not = function (children) {
    // eslint-disable-next-line no-param-reassign
    children = collectChildren(children);
    return children.filter(function (child) {
      return child.type !== fn;
    });
  };
  return fn;
};
export default Slot;